/**
 * api/usePoll — the real-time data hook for the SPA.
 *
 * The dashboard backend does not (yet) expose an SSE stream, so this scaffold ships a
 * short-poll strategy behind a hook whose surface is stream-agnostic: swapping to
 * `EventSource` later means changing this file only, not its callers. Each tick
 * refetches through the api client; in-flight requests are aborted on unmount / retune
 * so a slow response can never overwrite a newer one.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { UnauthorizedError } from './client';

export interface PollState<T> {
  readonly data: T | null;
  readonly error: string | null;
  /** True until the first successful (or failed) fetch resolves. */
  readonly loading: boolean;
  /** Epoch ms of the last successful fetch, or null. */
  readonly updatedAt: number | null;
  /** Force an immediate refetch (e.g. after a mutation). */
  readonly refresh: () => void;
}

export interface PollOptions {
  /** Poll interval in ms (default 5000). */
  readonly intervalMs?: number;
  /** When false, polling pauses (e.g. tab hidden or logged out). */
  readonly enabled?: boolean;
  /** Called once when a fetch rejects with 401 so the shell can log out. */
  readonly onUnauthorized?: () => void;
}

export function usePoll<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  opts: PollOptions = {},
): PollState<T> {
  const { intervalMs = 5000, enabled = true, onUnauthorized } = opts;

  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);

  // Keep the latest fetcher/callback without retriggering the effect each render.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const onUnauthRef = useRef(onUnauthorized);
  onUnauthRef.current = onUnauthorized;

  const [nonce, setNonce] = useState(0);
  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let controller: AbortController | undefined;

    const tick = async (): Promise<void> => {
      controller = new AbortController();
      try {
        const next = await fetcherRef.current(controller.signal);
        if (cancelled) return;
        setData(next);
        setError(null);
        setUpdatedAt(Date.now());
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === 'AbortError')) return;
        if (err instanceof UnauthorizedError) {
          onUnauthRef.current?.();
          return; // stop polling; the shell will unmount us on logout
        }
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) {
          setLoading(false);
          timer = setTimeout(() => void tick(), intervalMs);
        }
      }
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      controller?.abort();
    };
  }, [enabled, intervalMs, nonce]);

  return { data, error, loading, updatedAt, refresh };
}
